import { TextField, Typography, Button } from "@mui/material";
import { Box } from "@mui/system";
import DrawerWrapper from "components/DrawerWrapper";
import LoadingButton from "components/LoadingButton";
import { snack } from "components/toast";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DialogProps } from "types";
import {
    verifyMcaV3Credentials,
    verifyMcaV3Otp,
    resendMcaV3Otp,
} from "api/services/organization";

interface StateProps {
    mcaV3UserId: string;
    mcaV3Password: string;
}

interface Props extends DialogProps {
    existingData?: {
        mcaV3UserId?: string;
        mcaV3Password?: string;
    };
}

const initialState: StateProps = {
    mcaV3UserId: "",
    mcaV3Password: "",
};

function AddMcaV3CredGlasse({ open, setOpen, existingData }: Props) {
    const queryClient = useQueryClient();
    const formRef = useRef<HTMLFormElement>(null);

    const [state, setState] = useState<StateProps>(initialState);
    const [otp, setOtp] = useState("");
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [maskedMobile, setMaskedMobile] = useState("");

    const isUpdate = Boolean(existingData?.mcaV3UserId);

    useEffect(() => {
        if (existingData) {
            setState({
                mcaV3UserId: existingData.mcaV3UserId || "",
                mcaV3Password: existingData.mcaV3Password || "",
            });
        }
    }, [existingData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState({ ...state, [e.target.name]: e.target.value });
    };

    // STEP 1: Verify Credentials
    const { mutate: verifyCredentials, isLoading } = useMutation(
        verifyMcaV3Credentials,
        {
            onSuccess: (res: any) => {
                if (res?.data?.status === "otp_required") {
                    setSessionId(res.data.session_id);
                    setMaskedMobile(res.data.masked_mobile);
                    snack.success("OTP sent to registered mobile");
                } else {
                    snack.success("Credentials verified and saved successfully");
                    closeDrawer();
                }
            },
            onError: (err: any) => {
                snack.error(err?.response?.data?.message || "Verification failed");
            },
        }
    );

    // STEP 2: Verify OTP
    const { mutate: verifyOtp, isLoading: otpLoading } = useMutation(
        verifyMcaV3Otp,
        {
            onSuccess: () => {
                snack.success("OTP verified and credentials saved");
                closeDrawer();
            },
            onError: (err: any) => {
                snack.error(err?.response?.data?.message || "OTP verification failed");
            },
        }
    );

    const closeDrawer = () => {
        setOpen(false);
        setState(initialState);
        setOtp("");
        setSessionId(null);
        formRef.current?.reset();
        queryClient.invalidateQueries("org-mca-credentials");
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!state.mcaV3UserId.trim()) {
            snack.error("MCA V3 User ID is required");
            return;
        }

        if (!state.mcaV3Password.trim()) {
            snack.error("MCA V3 Password is required");
            return;
        }

        verifyCredentials(state);
    };

    const handleOtpSubmit = () => {
        if (!otp.trim()) {
            snack.error("Please enter OTP");
            return;
        }

        verifyOtp({
            session_id: sessionId,
            otp,
            mcaV3UserId: state.mcaV3UserId,
            mcaV3Password: state.mcaV3Password,
        });
    };

    const handleResendOtp = () => {
        resendMcaV3Otp({ session_id: sessionId })
            .then(() => snack.success("OTP resent successfully"))
            .catch(() => snack.error("Failed to resend OTP"));
    };

    return (
        <DrawerWrapper
            open={open}
            setOpen={setOpen}
            title={
                isUpdate
                    ? "Edit MCA V3 Portal Credentials"
                    : "Add MCA V3 Portal Credentials"
            }
        >
            <Box
                sx={{
                    background: "linear-gradient(135deg, #f8fbff 0%, #eef3ff 100%)",
                    minHeight: "100%",
                    p: 2,
                    borderRadius: 3,
                }}
            >
                <Box
                    sx={{
                        backdropFilter: "blur(16px)",
                        background: "rgba(255,255,255,0.6)",
                        border: "1px solid rgba(255,255,255,0.4)",
                        borderRadius: 4,
                        p: 3,
                        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.08)",
                    }}
                >
                    <form onSubmit={handleSubmit} ref={formRef}>
                        <TextField
                            sx={{
                                mt: 1,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255,255,255,0.7)",
                                },
                            }}
                            fullWidth
                            name="mcaV3UserId"
                            value={state.mcaV3UserId}
                            required
                            onChange={handleChange}
                            size="small"
                            label="MCA V3 User ID"
                        />

                        <TextField
                            sx={{
                                mt: 3,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    backgroundColor: "rgba(255,255,255,0.7)",
                                },
                            }}
                            fullWidth
                            required
                            name="mcaV3Password"
                            type="password"
                            value={state.mcaV3Password}
                            onChange={handleChange}
                            size="small"
                            label="MCA V3 Password"
                        />

                        {/* OTP Section */}
                        {sessionId && (
                            <>
                                <Typography
                                    mt={3}
                                    fontSize={13}
                                    sx={{ opacity: 0.7 }}
                                >
                                    Enter OTP sent to {maskedMobile}
                                </Typography>

                                <TextField
                                    sx={{
                                        mt: 1,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                            backgroundColor: "rgba(255,255,255,0.7)",
                                        },
                                    }}
                                    fullWidth
                                    size="small"
                                    label="Enter OTP"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                />

                                <Box mt={2} display="flex" gap={2}>
                                    <LoadingButton
                                        loading={otpLoading}
                                        onClick={handleOtpSubmit}
                                        title="Verify OTP"
                                        color="secondary"
                                        sx={{
                                            borderRadius: 2,
                                            px: 3,
                                            backdropFilter: "blur(10px)",
                                            background:
                                                "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                            boxShadow: "0 4px 14px rgba(99,102,241,0.3)",
                                        }}
                                    />
                                    <Button
                                        onClick={handleResendOtp}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: "none",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Resend OTP
                                    </Button>
                                </Box>
                            </>
                        )}

                        {!sessionId && (
                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <LoadingButton
                                    loading={isLoading}
                                    type="submit"
                                    title="Verify & Save"
                                    color="secondary"
                                    sx={{
                                        borderRadius: 2,
                                        px: 4,
                                        backdropFilter: "blur(10px)",
                                        background:
                                            "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                        boxShadow: "0 4px 14px rgba(99,102,241,0.3)",
                                    }}
                                />
                            </Box>
                        )}
                    </form>
                </Box>
            </Box>
        </DrawerWrapper>
    );
}

export default AddMcaV3CredGlasse;
